/**
 * HIS V4 · presence format (pure logic ไม่มี DOM — test ใน node ได้)
 *
 * เวลาทั้งหมดใน protocol เป็น epoch seconds (UTC)
 * การแสดงใช้ local time ของ browser + พุทธศักราช (เหมือน formatDateToStringBe ของทีม)
 * จึงไม่ lock วันจริงใน test (README HISV4: assertion เรื่องวันต้องใช้ pattern /^\d{2}\/\d{2}\/25\d\d$/)
 */

import type { PresencePeer } from "./presenceTypesV4";

const pad = (value: number): string => `${value}`.padStart(2, "0");

/** epoch seconds -> "DD/MM/YYYY HH:MM" (พ.ศ.) */
export function beDateTimeText(epochSeconds: number | undefined | null): string {
  const seconds = Number(epochSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return "-";
  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return "-";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear() + 543} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** 900 -> "15 นาที", 7200 -> "2 ชม. 1 นาที", 40 -> "ไม่ถึง 1 นาที" */
export function durationText(seconds: number | undefined | null): string {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  if (total < 60) return "ไม่ถึง 1 นาที";

  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  if (hours === 0) return `${minutes} นาที`;
  return minutes > 0 ? `${hours} ชม. ${minutes} นาที` : `${hours} ชม.`;
}

/**
 * label ภาษาไทยของ input ที่กำลังพิมพ์ (ใช้ id ของ CardDoctorV4/CardNursingV4)
 * id ที่ไม่รู้จัก -> แสดง id ดิบ (ช่วย dev มากกว่าปิดข้อมูล)
 */
export const PRESENCE_FIELD_LABELS: Record<string, string> = {
  hx_cc: "อาการนำ",
  hx_pi: "ประวัติการเจ็บป่วยปัจจุบัน",
  hx_past: "ประวัติการเจ็บป่วยในอดีต",
  hx_pers: "ประวัติส่วนตัว",
  hx_fam: "ประวัติครอบครัว",
  hx_modal_past: "ประวัติการเจ็บป่วยในอดีต",
  hx_modal_pers: "ประวัติส่วนตัว",
  hx_modal_fam: "ประวัติครอบครัว",
  hx_modal_sn: "บันทึกเฉพาะ",
  pe_other: "ตรวจร่างกายอื่น ๆ",
  pe_tpl: "แม่แบบตรวจร่างกาย",
  dx_pt: "ICD10 Term",
  dx_pc: "วินิจฉัยหลัก",
  dx_mt: "วินิจฉัยหลัก",
  dx_qp: "อาการของ วินิจฉัย",
  dx_qs: "อาการเพิ่มเติม",
  dx_cause: "สาเหตุเพิ่มเติม",
  dx_active: "ค้นหา ICD10",
  dx_approve_user: "ผู้ยืนยันวินิจฉัย",
  ph_t: "ประเภทภาพ",
  ph_c: "ไฟล์ภาพ",
  ph_cap: "คำอธิบายภาพ",
  nn_sn: "บันทึกการพยาบาล",
  nn_sf: "อาการขณะบันทึก",
  nn_shift: "เวรที่บันทึก",
  nn_order: "คำสั่งแพทย์ที่ผูก",
  nn_tpl: "แม่แบบบันทึกพยาบาล",
  nn_user: "ผู้บันทึก",
  md_problem: "ปัญหาที่บันทึก",
  md_plan: "แผนการดูแล",
  md_goal: "เป้าหมาย",
  md_d: "บันทึกทีมสหสาขา",
  md_user: "ผู้บันทึก",
};

export function fieldLabelOf(fieldId: string | null | undefined): string {
  const id = String(fieldId || "").trim();
  if (!id) return "";
  return PRESENCE_FIELD_LABELS[id] || id;
}

/** ตำแหน่งที่แสดงบน chip (backend ส่ง role เป็น GENERAL,DOCTOR,...) */
export function roleLabelOf(role: string | undefined | null): string {
  const names = String(role || "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
  if (!names.length) return "ผู้ใช้งาน";

  const labelOf = (name: string): string => {
    const key = name.toUpperCase();
    if (key === "DOCTOR" || key === "STUDENT_DOCTOR" || key === "GENERAL_PRACTITIONER") return "แพทย์";
    if (key.includes("NURSE")) return "พยาบาล";
    if (key.includes("PHARMAC")) return "เภสัชกร";
    if (key.includes("DENTIST")) return "ทันตแพทย์";
    if (key.includes("THERAPIST") || key.includes("PHYSIC")) return "นักกายภาพ/วิชาชีพ";
    if (key.includes("REGISTER") || key === "RECEPTION") return "ทะเบียน";
    return name;
  };
  const unique = Array.from(new Set(names.map(labelOf)));
  return unique.join("/");
}

export type PeerSummary = {
  /** ใช้เป็น tone ของ ChipV4 + เรียงแถว */
  tone: "amber" | "sky" | "warn";
  text: string;
  title: string;
};

/** ข้อความต่อ 1 peer: "กำลังพิมพ์: อาการนำ" หรือ "เปิดหน้านี้ไว้ตั้งแต่ 02/11/2568 09:12 (18 นาที)" */
export function peerSummaryOf(peer: PresencePeer, nowEpochSec?: number): PeerSummary {
  const opened = beDateTimeText(peer.opened_at);
  const duration = durationText(peer.seconds_open);

  if (peer.state === "TYPING") {
    const label = fieldLabelOf(peer.field);
    return {
      tone: "amber",
      text: `กำลังพิมพ์${label ? `: ${label}` : ""}`,
      title: `${roleLabelOf(peer.role)} · กำลังพิมพ์${label ? ` (${label})` : ""} · เปิดไว้ตั้งแต่ ${opened} (${duration})`,
    };
  }
  if (peer.state === "ACTIVE") {
    return {
      tone: "sky",
      text: "กำลังแก้ไขหน้านี้",
      title: `${roleLabelOf(peer.role)} · กำลังแก้ไข · เปิดไว้ตั้งแต่ ${opened} (${duration})`,
    };
  }
  return {
    tone: "warn",
    text: `เปิดหน้านี้ไว้ตั้งแต่ ${opened} (${duration})`,
    title: `${roleLabelOf(peer.role)} · เปิดหน้านี้ไว้ตั้งแต่ ${opened} · ยังเห็นล่าสุด ${beDateTimeText(
      peer.last_seen ?? nowEpochSec,
    )}`,
  };
}

/** แถบต้องไม่เด้งทุกวินาที: นับเฉพาะ peer ที่ไม่ใช่ tab ปัจจุบัน */
export const othersOf = (peers: PresencePeer[]): PresencePeer[] => peers.filter((peer) => !peer.self);
